import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Languages } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { knowledgeApi, knowledgeKeys, NotEnglish, refusal, type KnowledgeEntry, type KnowledgeOptions, type KnowledgeReport, type Scope } from '../api'
import { ScopePicker } from './ScopePicker'

export const TEXT_MAX = 1000

/**
 * Knowledge written on the Knowledge Base screen: added, edited, or written to correct a "Not
 * right" report. Saved in English: text in another language comes back as English to check first.
 */
export function KnowledgeEntryDialog({ open, onOpenChange, options, entry = null, report = null }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: KnowledgeOptions
  /** Editing this entry. */
  entry?: KnowledgeEntry | null
  /** Correcting this report: the new knowledge closes it. */
  report?: KnowledgeReport | null
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [agentId, setAgentId] = useState('')
  const [scope, setScope] = useState<Scope>({ objectId: null, technologyId: null })
  const [retirePaused, setRetirePaused] = useState(true)
  const [english, setEnglish] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setText(entry?.Text ?? report?.WhatRight ?? '')
    setAgentId(entry?.AgentID ?? report?.AgentID ?? '')
    setScope({ objectId: entry?.ObjectGone ? null : entry?.ObjectDbID ?? null, technologyId: entry?.TechnologyGone ? null : entry?.TechnologyID ?? null })
    setRetirePaused(true)
    setEnglish(false)
    setError(null)
  }, [open, entry, report])

  const save = useMutation({
    mutationFn: async () => {
      const body = { text: text.trim(), agentId, ...scope }
      if (entry) {
        const changed: Parameters<typeof knowledgeApi.update>[1] = {}
        if (body.text !== entry.Text) changed.text = body.text
        if (agentId !== entry.AgentID) changed.agentId = agentId
        if (scope.objectId !== entry.ObjectDbID || scope.technologyId !== entry.TechnologyID) Object.assign(changed, scope)
        if (!Object.keys(changed).length) return
        return knowledgeApi.update(entry.EntryID, changed)
      }
      return knowledgeApi.add({ ...body, ...(report ? { reportId: report.ReportID, retirePaused } : {}) })
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: knowledgeKeys.all }); onOpenChange(false) },
    onError: (e) => {
      if (e instanceof NotEnglish) { setText(e.english[0] ?? text); setEnglish(true); setError(null); return }
      setError(refusal(e) ?? t('knowledgeBase.saveFailed', 'It could not be saved. Try again.'))
    },
  })

  const ready = text.trim().length > 0 && !!agentId && (scope.objectId != null || scope.technologyId != null)
  const title = entry
    ? t('knowledgeBase.editTitle', 'Edit knowledge')
    : report ? t('knowledgeBase.correctTitle', 'Add the correct knowledge') : t('knowledgeBase.addTitle', 'Add knowledge')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t('knowledgeBase.entryIntro', 'One fact about this estate that the AI agents should know, in English. Not general knowledge any AI model has, and never a password or other secret.')}
          </DialogDescription>
        </DialogHeader>

        {report && (
          <div className="rounded-md border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-xs dark:bg-amber-950/30">
            <div className="font-medium">{t('knowledgeBase.reportedWrong', 'Reported as not right')}</div>
            <div className="mt-1">{report.WhatWrong}</div>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kb-text">{t('knowledgeBase.fact', 'Fact')}</Label>
            <Textarea
              id="kb-text"
              value={text}
              maxLength={TEXT_MAX}
              rows={3}
              onChange={(e) => { setText(e.target.value); setEnglish(false) }}
            />
            {english && (
              <p className="flex items-start gap-1.5 text-xs text-sky-700 dark:text-sky-300" role="status">
                <Languages className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t('knowledgeBase.englishCheck', 'The knowledge base is kept in English. This is the English that will be saved - check it, then save.')}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('knowledgeBase.agent', 'AI agent')}</Label>
            <Select value={agentId || undefined} onValueChange={setAgentId}>
              <SelectTrigger aria-label={t('knowledgeBase.agent', 'AI agent')}>
                <SelectValue placeholder={t('knowledgeBase.chooseAgent', 'Choose the AI agent')} />
              </SelectTrigger>
              <SelectContent>
                {options.agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('knowledgeBase.appliesTo', 'Applies to')}</Label>
            <ScopePicker value={scope} onChange={setScope} options={options} />
          </div>

          {report && report.PausedEntries > 0 && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={retirePaused} onCheckedChange={(v) => setRetirePaused(v === true)} className="mt-0.5" />
              <span>{t('knowledgeBase.retirePaused', 'Retire the knowledge this report stopped using - it was what the answer got wrong')}</span>
            </label>
          )}

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
            {save.isPending ? t('knowledgeBase.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
